import { useLazyQuery } from '@moonpig/web-core-graphql'
import { useEffect, useState } from 'react'
import { GetPreviousPageBreadcrumbTitles } from '../../../queries/getPreviousPageBreadcrumbTitles'
import {
  GetPreviousPageBreadcrumbTitlesQuery as GetPreviousPageBreadcrumbTitlesData,
  GetPreviousPageBreadcrumbTitlesQueryVariables,
} from '../../../queries/__generated__/getPreviousPageBreadcrumbTitles'
import {
  GetPreviousPageBreadcrumbTitles_page_Page_modules as PageModules,
  GetPreviousPageBreadcrumbTitles_page_Page_modules_ModuleBreadcrumbs as ModuleBreadcrumbs,
} from '../../../queries/types-graphql'

const isSameHost = (previousUrl: URL, currentUrl: URL) =>
  previousUrl.host === currentUrl.host

const isSubpath = (previousUrl: URL, _currentUrl: URL, parentPath: string) =>
  previousUrl.pathname.includes(parentPath)

const isNotSamePath = (
  previousUrl: URL,
  _currentUrl: URL,
  parentPath: string,
) => previousUrl.pathname !== parentPath

const validators: ((
  previousUrl: URL,
  currentUrl: URL,
  parentPath: string,
) => boolean)[] = [isSameHost, isSubpath, isNotSamePath]

const extractBreadcrumbTitle = (data?: GetPreviousPageBreadcrumbTitlesData) => {
  const page = data?.page
  if (page?.__typename !== 'Page') return null

  const breadcrumbsModule = page.modules?.find(
    ({ __typename }: PageModules) => __typename === 'ModuleBreadcrumbs',
  ) as ModuleBreadcrumbs | undefined

  const breadcrumbs = breadcrumbsModule?.breadcrumbs

  if (!breadcrumbs) return null

  return breadcrumbs[breadcrumbs.length - 1]?.title
}

export const usePreviousItems = ({
  storeId,
  departmentSlug,
}: {
  storeId: string
  departmentSlug: string
}) => {
  const [urls, setUrls] = useState<{
    current: string
    previous: string
  } | null>(null)

  const [getPageContent, { called, data }] = useLazyQuery<
    GetPreviousPageBreadcrumbTitlesData,
    GetPreviousPageBreadcrumbTitlesQueryVariables
  >(GetPreviousPageBreadcrumbTitles)

  useEffect(() => {
    const current = window.location.href
    const previous = document.referrer
    setUrls({ current, previous })
  }, [setUrls])

  if (!urls?.previous || !urls?.current) {
    return []
  }

  const parsedPreviousUrl = new URL(urls.previous)
  const parsedCurrentUrl = new URL(urls.current)

  if (
    !validators.every(validator =>
      validator(
        parsedPreviousUrl,
        parsedCurrentUrl,
        `/${storeId}/${departmentSlug}/`,
      ),
    )
  ) {
    return []
  }

  if (!called) {
    getPageContent({
      variables: { path: parsedPreviousUrl.pathname },
    })
  }

  const previousLabel = extractBreadcrumbTitle(data)
  if (!previousLabel) return []

  return [
    {
      url: parsedPreviousUrl.pathname,
      label: previousLabel,
    },
  ]
}
